/**
 * Gerador de PDF para a Projeção do Investimento Imobiliário
 * Abre uma janela formatada para impressão/salvamento como PDF
 */

const formatCurrency = (value) => {
  if (value === undefined || value === null) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value);
};

const formatPercent = (value, digits = 2) => {
  if (value === undefined || value === null) return '0,00%';
  return Number(value).toLocaleString('pt-BR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }) + '%';
};

export const generatePdf = (result, formData, tese = null) => {
  if (!result) return;

  const mesFinal = result.projecoes?.[result.projecoes.length - 1];
  const nomeCliente = formData.nome_cliente || 'N/A';
  const empreendimento = formData.empreendimento || 'N/A';
  const dataHoje = new Date().toLocaleDateString('pt-BR');
  const dataValidade = new Date(Date.now() + 72 * 60 * 60 * 1000).toLocaleDateString('pt-BR');
  const horaGeracao = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const logoUrl = window.location.origin + '/logo.svg';

  // Adição 6 — Status da proposta
  const propostaStatusMap = {
    sim:     { label: 'Proposta Aprovada pela Construtora', color: '#064E3B', bg: '#d1fae5' },
    nao:     { label: 'Proposta Não Aprovada',              color: '#7f1d1d', bg: '#fee2e2' },
    analise: { label: 'Proposta em Análise',                color: '#78350f', bg: '#fef3c7' },
  };
  const propostaStatus = formData.proposta_aprovada ? propostaStatusMap[formData.proposta_aprovada] : null;
  const propostaHtml = propostaStatus
    ? '<div style="display:inline-block;padding:4px 14px;border-radius:9999px;background:' + propostaStatus.bg + ';color:' + propostaStatus.color + ';font-size:12px;font-weight:700;margin-top:6px;">' + propostaStatus.label + '</div>'
    : '';

  // Adição 6 — Tese de Investimento
  const buildTeseHtml = () => {
    if (!tese || (!tese.cenarios?.length && !tese.veredicto)) return '';
    const cenariosRows = (tese.cenarios || []).map(c =>
      '<tr><td style="padding:8px 14px;border-bottom:1px solid #374151;color:#d1d5db;font-size:13px;">' + c.nome + '</td>'
      + '<td style="padding:8px 14px;border-bottom:1px solid #374151;color:#34d399;font-weight:700;font-size:13px;text-align:right;">' + c.resultado + '</td></tr>'
    ).join('');
    const alertasHtml = (tese.alertas || []).map(a =>
      '<p style="color:#fcd34d;font-size:11px;margin:4px 0;">⚠ ' + a + '</p>'
    ).join('');
    const veredictoColor = (tese.veredicto || '').startsWith('RECOMENDADO') ? '#065f46'
      : (tese.veredicto || '').startsWith('NÃO') ? '#7f1d1d' : '#374151';
    return '<div style="background:#111827;border-radius:16px;padding:24px;margin:24px 0;page-break-inside:avoid;">'
      + '<div style="font-size:18px;font-weight:800;color:#fef3c7;margin-bottom:2px;">Tese de Investimento TesIQ</div>'
      + '<div style="font-size:11px;color:#6b7280;margin-bottom:20px;">Análise independente · O corretor não pode alterar este resultado</div>'
      + (cenariosRows ? '<div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:2px;color:#6b7280;margin-bottom:10px;">Cenários de Saída Projetados</div>'
        + '<table style="width:100%;border-collapse:collapse;margin-bottom:16px;">' + cenariosRows + '</table>' : '')
      + (tese.perfil_produto ? '<div style="font-size:12px;color:#9ca3af;line-height:1.6;margin-bottom:16px;">' + tese.perfil_produto + '</div>' : '')
      + (alertasHtml ? '<div style="margin-bottom:16px;">' + alertasHtml + '</div>' : '')
      + '<div style="background:' + veredictoColor + ';border-radius:10px;padding:16px;text-align:center;">'
      + '<div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:2px;color:#a7f3d0;margin-bottom:4px;">Veredicto TesIQ</div>'
      + '<div style="font-size:15px;font-weight:800;color:#ffffff;">' + tese.veredicto + '</div>'
      + '</div></div>';
  };
  const teseHtml = buildTeseHtml();

  // Balões
  let baloesHtml = '';
  if (result.baloes_gerados && result.baloes_gerados.length > 0) {
    const baloesRows = result.baloes_gerados.map(b =>
      `<tr>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e5e5;text-align:center;">M${b.mes}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e5e5;">${b.descricao}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e5e5;">${b.data}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e5e5;text-align:right;font-family:'Courier New',monospace;font-weight:600;">${formatCurrency(b.valor)}</td>
      </tr>`
    ).join('');
    const totalBaloes = result.baloes_gerados.reduce((s, b) => s + b.valor, 0);
    baloesHtml = `
      <div style="margin-top:24px;">
        <h3 style="font-size:15px;font-weight:700;color:#1c1917;margin-bottom:12px;padding-bottom:6px;border-bottom:2px solid #0f2a42;">Baloes / Marcos da Construcao</h3>
        <table style="width:100%;border-collapse:collapse;font-size:13px;">
          <thead>
            <tr style="background:#f5f5f4;">
              <th style="padding:8px 12px;text-align:center;color:#57534e;font-weight:600;">Mes</th>
              <th style="padding:8px 12px;text-align:left;color:#57534e;font-weight:600;">Descricao</th>
              <th style="padding:8px 12px;text-align:left;color:#57534e;font-weight:600;">Data</th>
              <th style="padding:8px 12px;text-align:right;color:#57534e;font-weight:600;">Valor</th>
            </tr>
          </thead>
          <tbody>
            ${baloesRows}
            <tr style="background:#eff6ff;font-weight:700;">
              <td colspan="3" style="padding:8px 12px;">Total Baloes</td>
              <td style="padding:8px 12px;text-align:right;font-family:'Courier New',monospace;">${formatCurrency(totalBaloes)}</td>
            </tr>
          </tbody>
        </table>
      </div>`;
  }

  // Cronograma rows
  const cronogramaRows = result.projecoes ? result.projecoes.map(p => {
    const hasReforco = p.reforco_corrigido > 0;
    return `<tr class="${hasReforco ? 'row-reforco' : ''}">
      <td>${p.mes}</td>
      <td>${p.data}</td>
      <td>${formatCurrency(p.parcela_corrigida)}</td>
      <td>${hasReforco ? formatCurrency(p.reforco_corrigido) : '-'}</td>
      <td>${formatCurrency(p.total_investido_corrigido)}</td>
      <td>${formatPercent(p.correcao_percentual)}</td>
      <td class="col-saldo">${formatCurrency(p.saldo_devedor)}</td>
      <td class="col-valor">${formatCurrency(p.valor_imovel)}</td>
      <td>${formatCurrency(p.ganho_capital_acumulado)}</td>
      <td class="col-green">${formatPercent(p.percentual_sobre_ci)}</td>
      <td class="col-green">${formatCurrency(p.ganho_real)}</td>
      <td class="col-lucro">${formatCurrency(p.lucro_liquido)}</td>
    </tr>`;
  }).join('') : '';

  const htmlContent = `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>TesIQ - Simulacao ${nomeCliente} - ${empreendimento}</title>
  <style>
    @page { margin: 20mm 15mm; size: A4; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      color: #1c1917;
      background: #fff;
      font-size: 13px;
      line-height: 1.5;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px 24px;
      background: #0f2a42;
      border-radius: 10px;
      margin-bottom: 20px;
    }
    .header-logo-box {
      display: flex;
      align-items: center;
    }
    .header-logo {
      height: 36px;
      object-fit: contain;
    }
    .header-title {
      font-size: 22px;
      font-weight: 800;
      color: #ffffff;
      letter-spacing: -0.5px;
    }
    .header-subtitle {
      font-size: 11px;
      color: #93c5fd;
      margin-top: 2px;
    }
    .header-date {
      text-align: right;
      font-size: 11px;
      color: #bfdbfe;
    }
    .client-box {
      background: #eff6ff;
      border: 1px solid #bfdbfe;
      border-radius: 8px;
      padding: 16px 20px;
      margin-bottom: 20px;
      display: flex;
      gap: 40px;
    }
    .client-box .field {
      flex: 1;
    }
    .client-box .label {
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 1px;
      color: #1a3a5c;
      font-weight: 600;
      margin-bottom: 2px;
    }
    .client-box .value {
      font-size: 16px;
      font-weight: 700;
      color: #0f2a42;
    }
    .section-title {
      font-size: 15px;
      font-weight: 700;
      color: #1c1917;
      margin-bottom: 12px;
      padding-bottom: 6px;
      border-bottom: 2px solid #0f2a42;
    }
    .params-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 10px;
      margin-bottom: 20px;
    }
    .param-card {
      background: #fafaf9;
      border: 1px solid #e7e5e4;
      border-radius: 6px;
      padding: 10px 12px;
      text-align: center;
    }
    .param-card .label {
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      color: #78716c;
      font-weight: 600;
    }
    .param-card .value {
      font-size: 14px;
      font-weight: 700;
      color: #1c1917;
      margin-top: 2px;
      font-family: 'Courier New', monospace;
    }
    .proposta-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 10px;
      margin-bottom: 20px;
    }
    .proposta-card {
      border-radius: 8px;
      padding: 12px;
      text-align: center;
    }
    .proposta-card .label {
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      font-weight: 600;
    }
    .proposta-card .value {
      font-size: 16px;
      font-weight: 700;
      font-family: 'Courier New', monospace;
      margin-top: 4px;
    }
    .proposta-card .perc {
      font-size: 10px;
      margin-top: 2px;
    }
    .card-ato { background: #fef2f2; border: 1px solid #fecaca; }
    .card-ato .label { color: #991b1b; }
    .card-ato .value { color: #991b1b; }
    .card-ato .perc { color: #b91c1c; }
    .card-parcela { background: #fafaf9; border: 1px solid #e7e5e4; }
    .card-parcela .label { color: #57534e; }
    .card-parcela .value { color: #1c1917; }
    .card-parcela .perc { color: #78716c; }
    .card-reforco { background: #fafaf9; border: 1px solid #e7e5e4; }
    .card-reforco .label { color: #57534e; }
    .card-reforco .value { color: #1c1917; }
    .card-reforco .perc { color: #78716c; }
    .card-financiamento { background: #eef2ff; border: 1px solid #c7d2fe; }
    .card-financiamento .label { color: #3730a3; }
    .card-financiamento .value { color: #312e81; }
    .card-financiamento .perc { color: #4f46e5; }
    .card-total { background: #0f2a42; }
    .card-total .label { color: #bfdbfe; }
    .card-total .value { color: #ffffff; }
    .card-total .perc { color: #93c5fd; }
    .results-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;
      margin-bottom: 20px;
    }
    .result-card {
      border-radius: 8px;
      padding: 14px;
      text-align: center;
    }
    .result-card .label {
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: 0.8px;
      font-weight: 600;
    }
    .result-card .value {
      font-size: 18px;
      font-weight: 800;
      font-family: 'Courier New', monospace;
      margin-top: 4px;
    }
    .result-card .sub {
      font-size: 10px;
      margin-top: 2px;
    }
    .card-highlight { background: #eff6ff; border: 2px solid #60a5fa; }
    .card-highlight .label { color: #1a3a5c; }
    .card-highlight .value { color: #0f2a42; }
    .card-highlight .sub { color: #2563a0; }
    .card-neutral { background: #fafaf9; border: 1px solid #e7e5e4; }
    .card-neutral .label { color: #57534e; }
    .card-neutral .value { color: #1c1917; }
    .card-neutral .sub { color: #78716c; }
    .card-green { background: linear-gradient(135deg, #eff6ff, #dbeafe); border: 2px solid #3b82f6; }
    .card-green .label { color: #1a3a5c; }
    .card-green .value { color: #0f2a42; }
    .card-green .sub { color: #2563a0; }
    .footer-validity {
      background: #fef3c7;
      border: 1px solid #fbbf24;
      border-radius: 6px;
      padding: 10px 16px;
      margin-bottom: 16px;
      text-align: center;
      font-size: 12px;
      font-weight: 600;
      color: #92400e;
    }
    .footer-dark {
      background: #0f2a42;
      border-radius: 10px;
      padding: 20px 24px;
    }
    .footer-company {
      display: flex;
      align-items: center;
      gap: 16px;
      padding-bottom: 14px;
      border-bottom: 1px solid rgba(255,255,255,0.15);
    }
    .footer-company-logo-box {
      display: flex;
      align-items: center;
    }
    .footer-company-logo {
      height: 30px;
      object-fit: contain;
    }
    .footer-company-info {
      font-size: 11px;
      color: #93c5fd;
      line-height: 1.6;
    }
    .footer-company-name {
      font-size: 14px;
      font-weight: 700;
      color: #ffffff;
      margin-bottom: 2px;
    }
    .footer-disclaimer {
      margin-top: 14px;
      font-size: 9px;
      color: #60a5fa;
      text-align: center;
      line-height: 1.5;
    }
    .footer {
      margin-top: 40px;
    }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .page-break { page-break-before: always; }
      .footer { break-inside: avoid; page-break-inside: avoid; }
    }
    .page-break { page-break-before: always; }
    .crono-table { width: 100%; border-collapse: collapse; font-size: 10px; }
    .crono-table th {
      padding: 6px 8px;
      font-weight: 600;
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      border-bottom: 2px solid #0f2a42;
      text-align: right;
      white-space: nowrap;
    }
    .crono-table th:nth-child(1), .crono-table th:nth-child(2) { text-align: center; }
    .crono-table td {
      padding: 4px 8px;
      border-bottom: 1px solid #e7e5e4;
      text-align: right;
      font-family: 'Courier New', monospace;
      font-size: 10px;
      white-space: nowrap;
    }
    .crono-table td:nth-child(1), .crono-table td:nth-child(2) { text-align: center; font-family: 'Segoe UI', Tahoma, sans-serif; }
    .crono-table tbody tr:nth-child(even) { background: #fafaf9; }
    .crono-table .col-saldo { color: #cc0000; }
    .crono-table .col-valor { color: #2563eb; font-weight: 600; }
    .crono-table .col-green { color: #059669; }
    .crono-table .col-lucro { color: #1d4ed8; font-weight: 700; }
    .crono-table .row-reforco { background: #fefce8 !important; }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="header-title">TesIQ</div>
      <div class="header-subtitle">Simulacao de Investimento Imobiliario</div>
    </div>
    <div class="header-date">
      Gerado em: ${dataHoje} as ${horaGeracao}
    </div>
  </div>

  <div class="client-box">
    <div class="field">
      <div class="label">Cliente</div>
      <div class="value">${nomeCliente}</div>
    </div>
    <div class="field">
      <div class="label">Empreendimento</div>
      <div class="value">${empreendimento}</div>
    </div>
  ${propostaHtml}
  </div>

  <h3 class="section-title">Parametros da Simulacao</h3>
  <div class="params-grid">
    <div class="param-card">
      <div class="label">Valor Mercado</div>
      <div class="value">${formatCurrency(formData.valor_mercado)}</div>
    </div>
    <div class="param-card">
      <div class="label">Valor Compra</div>
      <div class="value">${formatCurrency(formData.valor_compra)}</div>
    </div>
    <div class="param-card">
      <div class="label">Valorizacao</div>
      <div class="value">${formData.valorizacao_anual}% a.a.</div>
    </div>
    <div class="param-card">
      <div class="label">Correcao Durante Obra</div>
      <div class="value">${formData.indice_construcao || 'CUB'} ${(Number(formData.correcao_anual_construcao) || Number(formData.correcao_anual) || 0).toFixed(2)}% a.a.</div>
    </div>
    <div class="param-card">
      <div class="label">Correcao Pos-Obra</div>
      <div class="value">${formData.indice_pos_obra || 'IPCA'} ${(Number(formData.correcao_anual_pos_obra) || Number(formData.correcao_anual) || 0).toFixed(2)}% a.a.</div>
    </div>
    <div class="param-card">
      <div class="label">Prazo</div>
      <div class="value">${formData.prazo_meses} meses</div>
    </div>
    <div class="param-card">
      <div class="label">Inicio</div>
      <div class="value">${formData.data_inicio}</div>
    </div>
    <div class="param-card">
      <div class="label">Parcela Mensal</div>
      <div class="value">${formatCurrency(result.parcela_mensal)}</div>
    </div>
    <div class="param-card">
      <div class="label">Conclusao Obra</div>
      <div class="value">${formData.conclusao_obra || 'N/A'}</div>
    </div>
  </div>

  <h3 class="section-title">Estrutura da Proposta</h3>
  <div class="proposta-grid">
    ${(result.sinal || 0) > 0 ? `
    <div class="proposta-card card-ato">
      <div class="label">Sinal (Pre-contrato)</div>
      <div class="value">${formatCurrency(result.sinal)}</div>
      <div class="perc">${formatPercent(result.sinal_percentual)}</div>
    </div>
    <div class="proposta-card card-ato">
      <div class="label">Valor no Ato</div>
      <div class="value">${formatCurrency(result.valor_no_ato)}</div>
      <div class="perc">${formatPercent(result.valor_no_ato_percentual)}</div>
    </div>` : `
    <div class="proposta-card card-ato">
      <div class="label">Ato / Entrada</div>
      <div class="value">${formatCurrency(result.ato_sinal)}</div>
      <div class="perc">${formatPercent(result.ato_sinal_percentual)}</div>
    </div>`}
    <div class="proposta-card card-parcela">
      <div class="label">Parcelas</div>
      <div class="value">${formatCurrency(result.parcelas_total)}</div>
      <div class="perc">${formatPercent(result.parcelas_percentual)}</div>
    </div>
    <div class="proposta-card card-reforco">
      <div class="label">Reforcos</div>
      <div class="value">${formatCurrency(result.reforcos_total)}</div>
      <div class="perc">${formatPercent(result.reforcos_percentual)}</div>
    </div>
    ${(result.parcela_chaves_valor || 0) > 0 ? `
    <div class="proposta-card card-reforco">
      <div class="label">Parcela das Chaves</div>
      <div class="value">${formatCurrency(result.parcela_chaves_valor)}</div>
      <div class="perc">${formatPercent(result.parcela_chaves_percentual || 0)}</div>
    </div>` : ''}
    ${(formData?.tipo_financiamento === 'bancario' && (result.financiamento_bancario || 0) > 0) ? `
    <div class="proposta-card card-financiamento">
      <div class="label">Financ. Pos Obra</div>
      <div class="value">${formatCurrency(result.financiamento_bancario)}</div>
      <div class="perc">${formatPercent(result.financiamento_bancario_percentual || 0)}</div>
    </div>` : ''}
    ${(result.veiculos_total || 0) > 0 ? `
    <div class="proposta-card card-reforco">
      <div class="label">Veiculos no Negocio</div>
      <div class="value">${formatCurrency(result.veiculos_total)}</div>
      <div class="perc">${formatPercent(result.veiculos_percentual || 0)}</div>
    </div>` : ''}
    <div class="proposta-card card-total">
      <div class="label">Total</div>
      <div class="value">${formatCurrency(result.total_proposta)}</div>
      <div class="perc">${formatPercent((formData.valor_compra && formData.valor_compra > 0) ? (result.total_proposta / formData.valor_compra * 100) : 0)}</div>
    </div>
  </div>

  ${baloesHtml}

  <h3 class="section-title" style="margin-top:24px;">Resultado Final (${formData.prazo_meses} meses)</h3>
  <div class="results-grid">
    <div class="result-card card-highlight">
      <div class="label">Valor do Imovel</div>
      <div class="value">${formatCurrency(result.valor_imovel_final)}</div>
      <div class="sub">Apos valorizacao</div>
    </div>
    <div class="result-card card-neutral">
      <div class="label">Total Investido</div>
      <div class="value">${formatCurrency(mesFinal?.total_investido_corrigido || 0)}</div>
      <div class="sub">Corrigido por ${formData.indice_construcao || 'CUB'} (obra) + ${formData.indice_pos_obra || 'IPCA'} (pos-obra)</div>
    </div>
    <div class="result-card card-neutral">
      <div class="label">Ganho de Capital</div>
      <div class="value">${formatCurrency(result.ganho_capital_final)}</div>
      <div class="sub">Valorizacao acumulada</div>
    </div>
    <div class="result-card card-green">
      <div class="label">Ganho Real</div>
      <div class="value">${formatCurrency(result.ganho_real_final)}</div>
      <div class="sub">Imovel - Saldo devedor</div>
    </div>
    <div class="result-card card-green">
      <div class="label">Lucro Liquido</div>
      <div class="value">${formatCurrency(result.lucro_liquido_final)}</div>
      <div class="sub">Ganho real - Investido</div>
    </div>
    <div class="result-card card-highlight">
      <div class="label">Retorno s/ Invest.</div>
      <div class="value">${formatPercent(result.percentual_sobre_ci_final)}</div>
      <div class="sub">Sobre capital investido</div>
    </div>
  </div>

  <div class="page-break"></div>

  <div class="header">
    <div>
      <div class="header-title">Cronograma de Pagamentos</div>
      <div class="header-subtitle">${nomeCliente} - ${empreendimento}</div>
    </div>
    <div class="header-date">
      ${formData.prazo_meses} meses | ${formData.valorizacao_anual}% val. | ${formData.indice_construcao || 'CUB'} ${(Number(formData.correcao_anual_construcao) || Number(formData.correcao_anual) || 0).toFixed(2)}% (obra) | ${formData.indice_pos_obra || 'IPCA'} ${(Number(formData.correcao_anual_pos_obra) || Number(formData.correcao_anual) || 0).toFixed(2)}% (pos)
    </div>
  </div>

  <table class="crono-table">
    <thead>
      <tr>
        <th>Prazo</th>
        <th>Data</th>
        <th>Parcela (Corrigida)</th>
        <th>Reforco</th>
        <th>Total Investido</th>
        <th>Correcao</th>
        <th class="col-saldo">Saldo Devedor</th>
        <th class="col-valor">Valorizacao</th>
        <th>Ganho Capital</th>
        <th class="col-green">% s/ C.I.</th>
        <th class="col-green">Ganho Real</th>
        <th class="col-lucro">Lucro Liquido</th>
      </tr>
    </thead>
    <tbody>
      ${cronogramaRows}
    </tbody>
  </table>



  <!-- Análise Financeira: TIR, VPL, Payback, Custo de Oportunidade -->
  ${(() => {
    const tir = result.tir_anual;
    const vpl = result.vpl;
    const paybackMes = result.payback_mes;
    const paybackData = result.payback_data;
    const cdoFinal = result.custo_oportunidade_valor_final;
    const cdoDelta = result.custo_oportunidade_delta;
    const cdoPct = result.custo_oportunidade_percentual;
    const taxaDesc = result.taxa_desconto_usada;
    const indDesc = formData.indice_desconto || 'CDI';
    const imoveiBate = cdoDelta != null && cdoDelta > 0;
    const temSAC = result.financiamento_bancario_parcelas?.length > 0;

    if (tir == null && vpl == null && paybackMes == null) return '';

    const fmtPct = (v, d=2) => v != null
      ? Number(v).toLocaleString('pt-BR',{minimumFractionDigits:d,maximumFractionDigits:d})+'%'
      : '—';

    return `
  <div style="margin-top:24px;">
    <div style="background:#04342c;border-radius:10px 10px 0 0;padding:14px 20px;display:flex;justify-content:space-between;align-items:center;">
      <div>
        <div style="font-size:13px;font-weight:700;color:#9ee4c4;letter-spacing:.5px;">ANÁLISE FINANCEIRA</div>
        <div style="font-size:10px;color:rgba(255,255,255,.5);margin-top:2px;">TIR · VPL · Payback · Custo de Oportunidade</div>
      </div>
      ${taxaDesc ? `<div style="text-align:right;"><div style="font-size:9px;text-transform:uppercase;letter-spacing:1px;color:#9ee4c4;">Taxa ref.</div><div style="font-size:14px;font-weight:700;font-family:'Courier New',monospace;color:#fff;">${fmtPct(taxaDesc)} a.a.</div><div style="font-size:9px;color:rgba(255,255,255,.5);">${indDesc} estimado</div></div>` : ''}
    </div>
    <div style="background:#fff;border:1px solid #e3ded0;border-top:none;border-radius:0 0 10px 10px;padding:0;">
      <div style="display:grid;grid-template-columns:repeat(4,1fr);border-bottom:1px solid #f0ede5;">
        ${[
          { label: 'TIR do Investimento', value: tir != null ? fmtPct(tir)+' a.a.' : '—', cor: tir != null && tir >= (taxaDesc||10.5) ? '#0b5346' : '#a32d2d', sub: tir != null && tir >= (taxaDesc||10.5) ? '▲ Supera referência' : '▼ Abaixo da referência' },
          { label: 'VPL', value: vpl != null ? formatCurrency(vpl) : '—', cor: vpl >= 0 ? '#0f6e5c' : '#a32d2d', sub: vpl >= 0 ? 'Viável' : 'VPL negativo' },
          { label: 'Payback', value: paybackMes != null ? Math.floor(paybackMes)+' meses' : 'Fora do prazo', cor: '#7a5a1f', sub: paybackData ? paybackData : '' },
          { label: 'vs. '+indDesc, value: cdoPct != null ? (cdoPct>0?'+':'')+fmtPct(cdoPct) : '—', cor: imoveiBate ? '#0b5346' : '#a32d2d', sub: cdoFinal ? 'RF: '+formatCurrency(cdoFinal) : '' },
        ].map(m => `
          <div style="padding:14px 16px;border-right:1px solid #f0ede5;">
            <div style="font-size:9px;text-transform:uppercase;letter-spacing:1px;color:#96958a;margin-bottom:6px;">${m.label}</div>
            <div style="font-size:15px;font-weight:700;font-family:'Courier New',monospace;color:${m.cor};">${m.value}</div>
            <div style="font-size:9px;color:#96958a;margin-top:3px;">${m.sub}</div>
          </div>`).join('')}
      </div>
      <div style="padding:12px 16px;background:${imoveiBate?'#eef8f3':'#fefce8'};border-radius:0 0 10px 10px;">
        <div style="font-size:11px;font-weight:600;color:${imoveiBate?'#0b5346':'#92400e'};margin-bottom:3px;">
          ${imoveiBate ? '✓ Investimento supera a renda fixa' : '⚠ Atenção ao custo de oportunidade'}
        </div>
        <div style="font-size:10px;color:${imoveiBate?'#0f6e5c':'#a16207'};line-height:1.5;">
          ${imoveiBate
            ? `TIR de ${fmtPct(tir)} supera ${fmtPct(taxaDesc)} a.a. de referência. VPL positivo: ${vpl!=null?formatCurrency(vpl):'—'}. Imóvel supera a renda fixa em ${fmtPct(Math.abs(cdoPct||0))}.`
            : `Com referência de ${fmtPct(taxaDesc)} a.a., o VPL ficou ${vpl!=null?formatCurrency(vpl):'negativo'}. O capital na renda fixa renderia ${cdoFinal?formatCurrency(cdoFinal):'mais'}.`}
        </div>
      </div>
    </div>
  </div>
  ${temSAC ? `
  <div style="margin-top:16px;background:#fff;border:1px solid #e3ded0;border-radius:10px;overflow:hidden;">
    <div style="background:#f7f5ef;padding:10px 16px;border-bottom:1px solid #e3ded0;">
      <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#6b6a5f;">Financiamento Bancário — ${formData.tipo_amortizacao||'PRICE'}</div>
    </div>
    <div style="display:grid;grid-template-columns:repeat(4,1fr);padding:14px 16px;gap:12px;">
      ${[
        ['Parcela inicial', formatCurrency(result.financiamento_bancario_parcela_inicial), '#14231d'],
        ['Parcela final',   formatCurrency(result.financiamento_bancario_parcela_final),   (formData.tipo_amortizacao||'PRICE')==='SAC'?'#0b5346':'#14231d'],
        ['Total de juros',  formatCurrency(result.financiamento_bancario_total_juros),     '#a32d2d'],
        ['CET anual', result.financiamento_bancario_cet_anual ? fmtPct(result.financiamento_bancario_cet_anual)+' a.a.' : '—', '#14231d'],
      ].map(([l,v,c]) => `<div><div style="font-size:9px;text-transform:uppercase;letter-spacing:1px;color:#96958a;margin-bottom:4px;">${l}</div><div style="font-size:13px;font-weight:700;font-family:'Courier New',monospace;color:${c};">${v}</div></div>`).join('')}
    </div>
  </div>` : ''}`;
  })()}


  <!-- Estimativa de Retorno com Locação -->
  ${(() => {
    const taxaLocacao = formData.locacao_percentual || 0.5;
    const mesRefEntrega = result.conclusao_obra_mes || formData.prazo_meses;
    const projecaoRef = result.projecoes?.find(p => p.mes === mesRefEntrega) || mesFinal;
    const valorImovelRef = projecaoRef?.valor_imovel || result.valor_imovel_final || 0;
    const aluguelMensal = formData.locacao_valor_fixo > 0
      ? formData.locacao_valor_fixo
      : (valorImovelRef * taxaLocacao) / 100;
    const totalInvestidoRef = projecaoRef?.total_investido_corrigido || 0;
    const retornoMensalCapital = totalInvestidoRef > 0 ? (aluguelMensal / totalInvestidoRef) * 100 : 0;
    const retornoAnualCapital  = retornoMensalCapital * 12;
    const mesesLocacao = result.conclusao_obra_mes > 0
      ? Math.max(0, formData.prazo_meses - result.conclusao_obra_mes)
      : 0;
    const rendimentoTotal = aluguelMensal * mesesLocacao;
    if (aluguelMensal <= 0) return '';
    return `
  <div style="margin-top:24px;background:linear-gradient(135deg,#1e3a5f,#0f2a42);border-radius:10px;padding:20px 24px;color:#fff;">
    <h3 style="font-size:15px;font-weight:700;color:#bfdbfe;margin-bottom:14px;">Estimativa de Retorno com Locacao</h3>
    <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:12px;">
      <div style="background:rgba(255,255,255,0.08);border-radius:8px;padding:12px;text-align:center;">
        <div style="font-size:9px;text-transform:uppercase;letter-spacing:1px;color:#93c5fd;">Aluguel Mensal</div>
        <div style="font-size:16px;font-weight:700;font-family:'Courier New',monospace;margin-top:4px;">${formatCurrency(aluguelMensal)}</div>
        <div style="font-size:9px;color:#bfdbfe;margin-top:2px;">${taxaLocacao}% s/ valor do imovel</div>
      </div>
      <div style="background:rgba(255,255,255,0.08);border-radius:8px;padding:12px;text-align:center;">
        <div style="font-size:9px;text-transform:uppercase;letter-spacing:1px;color:#93c5fd;">Retorno Mensal s/ Capital</div>
        <div style="font-size:16px;font-weight:700;font-family:'Courier New',monospace;margin-top:4px;">${retornoMensalCapital.toLocaleString('pt-BR',{minimumFractionDigits:2,maximumFractionDigits:2})}%</div>
        <div style="font-size:9px;color:#bfdbfe;margin-top:2px;">${retornoAnualCapital.toLocaleString('pt-BR',{minimumFractionDigits:2,maximumFractionDigits:2})}% a.a.</div>
      </div>
      <div style="background:rgba(255,255,255,0.08);border-radius:8px;padding:12px;text-align:center;">
        <div style="font-size:9px;text-transform:uppercase;letter-spacing:1px;color:#93c5fd;">Rendimento Pos-Obra</div>
        <div style="font-size:16px;font-weight:700;font-family:'Courier New',monospace;margin-top:4px;">${formatCurrency(rendimentoTotal)}</div>
        <div style="font-size:9px;color:#bfdbfe;margin-top:2px;">${mesesLocacao} meses de locacao</div>
      </div>
      <div style="background:rgba(255,255,255,0.08);border-radius:8px;padding:12px;text-align:center;">
        <div style="font-size:9px;text-transform:uppercase;letter-spacing:1px;color:#93c5fd;">Lucro c/ Locacao</div>
        <div style="font-size:16px;font-weight:700;font-family:'Courier New',monospace;margin-top:4px;">${formatCurrency(result.lucro_liquido_final + rendimentoTotal)}</div>
        <div style="font-size:9px;color:#bfdbfe;margin-top:2px;">Lucro liquido + rendimento</div>
      </div>
    </div>
  </div>`;
  })()}

  ${teseHtml}

  <div class="footer">
    <div class="footer-validity">
      As condicoes desta proposta tem validade de 72 horas a partir da data de emissao (${dataHoje} as ${horaGeracao}).
      Valida ate: ${dataValidade}.
    </div>
    <div class="footer-dark">
      <div class="footer-disclaimer">
        Simulacao gerada automaticamente pela Calculadora de Investimento TesIQ. Os valores apresentados sao projecoes baseadas nos parametros informados e nao constituem garantia de retorno. Valores sujeitos a alteracao sem aviso previo.
      </div>
    </div>
  </div>

</body>
</html>`;

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  }
};
